
import React, { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { MedewerkerResponse, MedewerkerInsert, MedewerkerUpdate } from "@/integrations/supabase/client";
import MedewerkerForm from "@/components/medewerkers/MedewerkerForm";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import { useMedewerkerQueries } from "@/hooks/medewerkers/useMedewerkerQueries";

interface MedewerkerFormDialogProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  selectedMedewerker: MedewerkerResponse | null;
  onSubmit: (data: MedewerkerInsert | MedewerkerUpdate) => void;
  onCancel: () => void;
  isSubmitting: boolean;
}

const MedewerkerFormDialog = ({
  isOpen,
  onOpenChange,
  selectedMedewerker,
  onSubmit,
  onCancel,
  isSubmitting
}: MedewerkerFormDialogProps) => {
  const [selectedPackers, setSelectedPackers] = useState<string[]>([]);
  const [availablePackers, setAvailablePackers] = useState<{id: string, naam: string, actief: boolean | null}[]>([]);
  const { getAssignedPackers, updateAssignedPackers } = useMedewerkerQueries();
  
  // Fetch available packers from Supabase
  useEffect(() => {
    const fetchPackers = async () => {
      try {
        const { data, error } = await fetch("https://bpxebrbhhywoayvqwdnh.supabase.co/rest/v1/inpakkers?select=id,naam,actief&order=naam", {
          headers: {
            "apikey": "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6ImJweGVicmJoaHl3b2F5dnF3ZG5oIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NDQ3OTQwNzUsImV4cCI6MjA2MDM3MDA3NX0.aMoMxNwfxI0MuSKxgYri4y1Xrf9tJtTflKAAyZzRRbE",
            "Content-Type": "application/json"
          }
        }).then(res => res.json());
        
        if (error) throw error;
        setAvailablePackers(data || []);
      } catch (error) {
        console.error("Error fetching packers:", error);
      }
    };

    if (isOpen) {
      fetchPackers();
    }
  }, [isOpen]);
  
  // Fetch assigned packers for selected medewerker
  useEffect(() => {
    const fetchAssignedPackers = async () => {
      if (selectedMedewerker) {
        const packers = await getAssignedPackers(selectedMedewerker.id);
        setSelectedPackers(packers);
      } else {
        setSelectedPackers([]);
      }
    };
    
    if (isOpen) {
      fetchAssignedPackers();
    }
  }, [isOpen, selectedMedewerker, getAssignedPackers]);

  const handlePackerToggle = (packerNaam: string) => {
    setSelectedPackers(prev => 
      prev.includes(packerNaam)
        ? prev.filter(p => p !== packerNaam)
        : [...prev, packerNaam]
    );
  };
  
  const handleFormSubmit = async (data: MedewerkerInsert | MedewerkerUpdate) => {
    onSubmit(data);
    
    // After submitting the medewerker data, update the packer assignments
    if (selectedMedewerker) {
      await updateAssignedPackers(selectedMedewerker.id, selectedPackers, selectedMedewerker.naam);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[600px]">
        <DialogHeader>
          <DialogTitle>
            {selectedMedewerker ? "Medewerker wijzigen" : "Medewerker toevoegen"}
          </DialogTitle>
        </DialogHeader>
        <div className="grid gap-6 md:grid-cols-2">
          <div>
            <MedewerkerForm
              medewerker={selectedMedewerker || undefined}
              onSubmit={handleFormSubmit}
              onCancel={onCancel}
              isSubmitting={isSubmitting}
            />
          </div>
          
          {/* Show packer selection only when editing an existing medewerker */}
          {selectedMedewerker && (
            <div>
              <h3 className="text-lg font-medium mb-2">Toegewezen inpakkers</h3>
              <p className="text-sm text-muted-foreground mb-4">
                Selecteer de inpakkers die deze medewerker mag gebruiken
              </p>
              
              <ScrollArea className="h-[300px] rounded-md border p-4">
                <div className="space-y-4">
                  {availablePackers
                    .filter(packer => packer.actief !== false)
                    .map(packer => (
                      <div key={packer.id} className="flex items-center space-x-2">
                        <Checkbox 
                          id={`packer-${packer.id}`}
                          checked={selectedPackers.includes(packer.naam)}
                          onCheckedChange={() => handlePackerToggle(packer.naam)}
                        />
                        <Label htmlFor={`packer-${packer.id}`} className="cursor-pointer">
                          {packer.naam}
                        </Label>
                      </div>
                    ))
                  }
                </div>
              </ScrollArea>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default MedewerkerFormDialog;
